"use client";

import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { PromocodeListItem } from "commonModule/services/booking/getListOfPromocodes";
import BookingsIcon from "components/icons-svg/BookingsIcon";
import CloseIcon from "components/icons-svg/CloseIcon";
import Hamburger from "components/icons-svg/Hamburger";
import ManageAgentsIcon from "components/icons-svg/ManageAgentsIcon";
import MessagesIcon from "components/icons-svg/MessagesIcon";
import NewProfileIcon from "components/icons-svg/NewProfileIcon";
import PromoCodeIcon from "components/icons-svg/PromoCodesIcon";
import QuestionMarkIcon from "components/icons-svg/QuestionMarkIcon";
import SignOutIcon from "components/icons-svg/SignOutIcon";
import SignupIcon from "components/icons-svg/SignupIcon";
import UserCircle from "components/icons-svg/UserCircle";
import WishlistIcon from "components/icons-svg/WishlistIcon";
import { TravelAgentLogo } from "components/layouts/Header/TravelAgentLogo";
import Divider from "components/utilities/Divider";
import usePriceDropAlertNotification from "hooks/swr/usePriceDropAlertNotification";
import { useIsAgent } from "hooks/useIsAgent";
import { useIsAgentOwner } from "hooks/useIsAgentOwner";
import { useSoftLoginDataFromLocalStorage } from "hooks/useSoftLoginDataFromLocalStorage";
import { PageOriginEnumV2 } from "services/mixpanel/types";
import { logOut, markAuthStore, selectIsUserLoggedIn } from "stores/auth/authSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
const AppDesktopHeaderMenu = ({
  unreadCount,
  promoCodesList,
  buttonVariantOutline = false,
  buttonTextClassName
}: {
  unreadCount: number;
  promoCodesList?: PromocodeListItem[];
  buttonVariantOutline?: boolean;
  buttonTextClassName?: string;
}) => {
  const {
    data: priceDropResponse
  } = usePriceDropAlertNotification();
  const priceDropCount = priceDropResponse?.data;
  const c = useTranslations("common");
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const {
    softLoginData
  } = useSoftLoginDataFromLocalStorage();
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  const isDetailsPage = pathname?.startsWith("/detail") || pathname?.startsWith("/multi-unit-detail");
  const page = isDetailsPage ? "/detail" : pathname?.startsWith("/vacation-rentals") ? "/vacation-rentals" : pathname;
  const onLoginButtonClick = () => {
    localStorage.setItem("mixpanelPageOrigin", PageOriginEnumV2[(page as keyof typeof PageOriginEnumV2)] || "header");
    if (!isPaymentPage) {
      dispatch(markAuthStore({
        ui: {
          isLoginFlow: true,
          isSigningUpWithEmail: true,
          modalType: "LOGIN_WITH_EMAIL"
        }
      }));
    }
  };
  const onpenSignupModal = ({
    clickedBtn
  }: {
    clickedBtn?: "FAV" | "MESSAGES";
  }) => {
    dispatch(markAuthStore({
      ui: {
        isLoginFlow: false,
        isSigningUpWithEmail: true,
        modalType: "SIGNUP_DETAILS_FORM"
      },
      emailId: softLoginData?.emailId || "",
      softLogin: {
        softLoginType: null,
        softSignupDescription: clickedBtn === "FAV" ? "Let’s create your account so you can access your collections." : clickedBtn === "MESSAGES" ? "Let’s create your account so you can access your messages." : "",
        softLoginPayload: {
          emailId: softLoginData?.emailId || ""
        }
      }
    }));
  };
  const onLogout = () => {
    dispatch(logOut());
  };
  const isAgent = useIsAgent();
  const isAgentOwner = useIsAgentOwner();
  const bookingsHref = isAgent ? "/agent/bookings" : "/booking";
  return <Menu as='div' className='relative' data-sentry-element="Menu" data-sentry-component="AppDesktopHeaderMenu" data-sentry-source-file="AppDesktopHeaderMenu.tsx">
      {({
      open,
      close
    }) => <>
          <Menu.Button className={`btn btn-secondary rounded-md px-2 py-2 hover:bg-[rgba(0,0,0,0.2)] hover:bg-none hover:shadow-none active:bg-[rgba(0,0,0,0.6)] lg:h-12 lg:rounded-full lg:px-4 ${buttonVariantOutline ? "border border-secondary-500 text-secondary-500 hover:text-white focus-visible:text-white" : `border-transparent ${buttonTextClassName ?? "text-white"} outline-none`} shadow-none`}>
            <div className='flex items-center gap-3 text-inherit lg:gap-4'>
              <div className='relative'>
                <UserCircle className='!text-white' />{" "}
                {!isLoggedIn && (softLoginData?.isSoftLogingFromFavOrPDA || softLoginData?.isSoftLogingFromMessageHost) ? <div className='min-w-4 absolute bottom-[-3px] right-[-6px] flex h-4 w-4 items-center justify-center rounded-full bg-red-500 px-[5px] py-[2px] text-xs font-bold leading-3 text-white'>
                    1
                  </div> : null}
              </div>

              <div className='relative text-inherit'>
                <Hamburger className='h-5 w-5 text-inherit' />
                {unreadCount > 0 && <div className='absolute -top-1.5 right-[-7px] flex h-[18px] w-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500'>
                    <p className='text-xs text-white'>
                      {unreadCount > 9 ? "9+" : priceDropCount ? unreadCount + 1 : unreadCount}
                    </p>
                  </div>}
                {priceDropCount && unreadCount === 0 && <div className='absolute -top-1 right-[-5px] flex h-3 w-3 min-w-[12px] items-center justify-center rounded-full bg-red-500' />}
              </div>
            </div>
          </Menu.Button>

          <Transition as={Fragment} show={open} enter='transition ease-out duration-100' enterFrom='transform opacity-0 scale-95' enterTo='transform opacity-100 scale-100' leave='transition ease-in duration-75' leaveFrom='transform opacity-100 scale-100' leaveTo='transform opacity-0 scale-95'>
            <Menu.Items className='min-w-auto absolute right-0 top-12 z-[99] w-[280px] rounded-[16px] bg-white pb-4 shadow-[0px_4px_20px_0px_#37384d21] outline-none xsm:w-[339px] lg:pt-4'>
              <div className='flex items-center justify-between gap-2 pb-4 pl-6 pr-2 pt-2 lg:hidden'>
                <TravelAgentLogo className='text-[#735721]' />
                <button aria-label='close search drawer' className='btn btn-ghost h-10 w-10 min-w-[40px] p-0' onClick={close}>
                  <CloseIcon className='h-7 w-7 text-[#8D91A5]' />
                </button>
              </div>
              {!isLoggedIn && <>
                  <Menu.Item as='button' onClick={() => {
              onLoginButtonClick();
              close();
            }} className='mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]'>
                    <NewProfileIcon className='fill-[#003C49]' />
                    Log in
                  </Menu.Item>
                  <Menu.Item>
                    <Link href='/signup' className='mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]' onClick={close}>
                      <SignupIcon className='text-[#003C49]' />
                      {c("btn.signup")}
                    </Link>
                  </Menu.Item>
                  {softLoginData?.isSoftLogingFromFavOrPDA || softLoginData?.isSoftLogingFromMessageHost ? <Divider className='mx-auto mb-2 w-[calc(100%-48px)] !opacity-100' /> : null}
                  {softLoginData?.isSoftLogingFromFavOrPDA && <Menu.Item as='button' onClick={() => {
              onpenSignupModal({
                clickedBtn: "FAV"
              });
              close();
            }} className='mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]'>
                      <WishlistIcon className='text-[#003C49]' />
                      Collections
                      <div className='min-w-4 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 px-[5px] py-[2px] text-xs font-bold leading-3 text-white'>
                        1
                      </div>
                    </Menu.Item>}
                  {softLoginData?.isSoftLogingFromMessageHost && <Menu.Item as='button' onClick={() => {
              onpenSignupModal({
                clickedBtn: "MESSAGES"
              });
              close();
            }} className='mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]'>
                      <MessagesIcon className='text-[#003C49]' />
                      Messages
                      <div className='min-w-4 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 px-[5px] py-[2px] text-xs font-bold leading-3 text-white'>
                        1
                      </div>
                    </Menu.Item>}
                </>}
              {isLoggedIn && <>
                  <Menu.Item>
                    <Link href='/my-profile' className={`${pathname === "/my-profile" ? "bg-[#F0F1F9]" : ""} mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close}>
                      <NewProfileIcon />
                      {c("words.profile")} {/* Profile */}
                    </Link>
                  </Menu.Item>

                  <Menu.Item>
                    <Link href='/favorites' className={`${pathname === "/favorites" ? "bg-[#F0F1F9]" : ""} mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close}>
                      <WishlistIcon className='text-[#003C49]' />
                      Collections
                      {priceDropCount && <div className='min-w-3 flex h-3 w-3 items-center justify-center rounded-full bg-red-500' />}
                    </Link>
                  </Menu.Item>

                  <Divider className='mx-auto mb-2 w-[calc(100%-48px)] !opacity-100' />
                  <Menu.Item>
                    <Link href={bookingsHref} className={`${pathname === bookingsHref ? "bg-[#F0F1F9]" : ""} mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close}>
                      <BookingsIcon className='text-[#003C49]' /> Bookings
                    </Link>
                  </Menu.Item>
                </>}

              {isAgent && isLoggedIn && <>
                  <Divider className='mx-auto mb-2 w-[calc(100%-48px)] !opacity-100' />
                  <Menu.Item>
                    <Link href='/agent/commissions' className={`${pathname === "/agent/commissions" ? "bg-[#F0F1F9]" : ""} mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close}>
                      <svg xmlns='http://www.w3.org/2000/svg' className={`h-6 w-6 text-[#003C49]`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z' />
                      </svg>
                      Commissions
                    </Link>
                  </Menu.Item>
                  {isAgentOwner && <Menu.Item>
                      <Link href='/agent/manage-agents' className={`${pathname === "/agent/manage-agents" ? "bg-[#F0F1F9]" : ""} mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close}>
                        <ManageAgentsIcon className='h-6 w-6 text-[#003C49]' />
                        Manage Agents
                      </Link>
                    </Menu.Item>}
                </>}

              {!isAgent && isLoggedIn && promoCodesList && promoCodesList?.length > 0 && <>
                    <Divider className='mx-auto mb-2 w-[calc(100%-48px)] !opacity-100' />
                    <Menu.Item>
                      <Link href='/promocodes' className={`${pathname === "/promocodes" ? "bg-[#F0F1F9]" : ""} mb-2 flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close} data-track-v1-event='PROMO_CODE_CLICKED' data-track-v1-props={JSON.stringify({
                Source: "Header"
              })}>
                        <PromoCodeIcon className='text-[#003C49]' /> Promo codes
                      </Link>
                    </Menu.Item>
                  </>}

              {!isAgent && <Menu.Item>
                  <Link href='/help-support-center' target='_blank' className={`${isLoggedIn ? "mb-2" : ""} flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]`} onClick={close}>
                    <QuestionMarkIcon className='text-[#003C49]' /> Help
                  </Link>
                </Menu.Item>}
              {isLoggedIn && <>
                  <Divider className='mx-auto mb-2 w-[calc(100%-48px)] !opacity-100' />
                  <Menu.Item as='button' onClick={() => {
              onLogout();
              close();
            }} className='flex h-14 w-full items-center gap-2 px-6 py-4 font-medium leading-normal text-brand-dark outline-none transition-all duration-500 active:bg-[#F0F1F9] md:hover:bg-[#F0F1F9]'>
                    <SignOutIcon className='text-[#003C49]' /> Sign out
                  </Menu.Item>
                </>}
            </Menu.Items>
          </Transition>
        </>}
    </Menu>;
};
export default AppDesktopHeaderMenu;